import {cloneDeep, orderBy, set, sortBy, uniqBy} from 'lodash-es';
import {useCallback, useEffect, useState} from 'react';

import {useCountryList} from '@/context/CountryListContext';
import type {CurrencyFilters} from '@/services/currencyService';
import {currencyService} from '@/services/currencyService';
import type {ProcessorCapabilityMatrix} from '@/services/processorService';
import {processorService} from '@/services/processorService';
import type {
  AfriexCountryCode,
  AfriexCurrency,
  AfriexTransactionProcessor,
} from '@/types';
import {CountryStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {RemoveProcessorRequest, UpdateProcessorRequest} from './types';
import {stripRestrictedProcessors, validateProcessor} from './utils';

const useProcessorList = () => {
  const [processorsAll, setProcessorsAll] = useState<
    AfriexTransactionProcessor[]
  >([]);
  const [capabilityMatrix, setCapabilityMatrix] =
    useState<ProcessorCapabilityMatrix>({});
  const [currencyList, setCurrencyList] = useState<AfriexCurrency[]>([]);
  const [isCurrencyListLoading, setIsCurrencyListLoading] =
    useState<boolean>(true);
  const [error, setError] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState<AfriexCurrency>();
  const [shouldShowUpdateForm, setShouldShowUpdateForm] =
    useState<boolean>(false);
  const {state: countryListState} = useCountryList();
  const {countryList, loading: isCountryListLoading} = countryListState;

  const activeCountries =
    countryList?.filter((c) => c.status === CountryStatus.ACTIVE) || [];

  const filteredCountryList = uniqBy(
    sortBy(activeCountries, 'currency'),
    'currency',
  );

  /**
   * One representative active country per currency, for the flag only.
   *
   * The table used to iterate this country list directly, which meant a
   * currency reached the screen only if some active country used it. Crypto
   * (USDC, USDT) has no country at all, so its corridors were invisible and
   * uneditable even though they carry live routing; a currency whose countries
   * were all inactive disappeared the same way. Rows now come from the currency
   * list and look their country up here, so a missing country costs a flag
   * rather than the whole row.
   */
  const countryCodeByCurrency = filteredCountryList.reduce<
    Record<string, AfriexCountryCode>
  >((acc, country) => {
    if (country?.currency) {
      acc[country.currency] = country.iso2;
    }
    return acc;
  }, {});

  const fetchProcessorAll = useCallback(async () => {
    try {
      const processors = await processorService.getProcessorAll();
      processors.sort();
      setProcessorsAll(processors);
    } catch (e) {
      showErrorMessage(
        `Fetching all processor list failed:${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  // This list is the row set for the table, so a currency missing from it is a
  // corridor nobody can see or edit. Page to the end rather than trusting one
  // fixed page to be big enough.
  const fetchCurrencyList = useCallback(async () => {
    try {
      const limit = 100;
      // Downloading the whole collection is deliberate here: there is one row
      // per currency (`code` is limited to AfriexCurrencyCode plus the crypto
      // symbols, well under 150), so this loop runs once or twice.
      //
      // Do not copy this loop for a collection that grows (transactions, fees,
      // users, audit events). Every page re-runs the server's count and a
      // longer skip, so each page costs more than the last, and the browser
      // ends up holding every row. For those, page on the server and render
      // one page at a time, stream the full range from one cursor (the
      // `?action=DOWNLOAD` exports built on streamCursorToConsumer), or, in a
      // server job, read in `_id` batches the way BatchTransactionProcessor
      // does.
      const collected: AfriexCurrency[] = [];
      let page = 0;
      for (;;) {
        // `columns` is a real parameter of this endpoint (`getList` even
        // defaults it), but `AfriexQueryParams`, which `CurrencyFilters`
        // aliases, does not declare it. Widened here rather than cast away;
        // the proper home for the field is `src/types/AfriexQueryParams.ts`.
        const {data, total} = await currencyService.getList({
          page,
          limit,
          columns: [
            'name',
            'code',
            'symbol',
            'isActive',
            'id',
            'business',
            'user',
            'api',
            'createdAt',
          ],
        } as CurrencyFilters & {columns: string[]});
        collected.push(...(data ?? []));
        const isLastPage =
          !data?.length ||
          data.length < limit ||
          collected.length >= (total ?? collected.length);
        // The page guard is a backstop: a wrong `total` should not spin forever.
        if (isLastPage || page >= 20) {
          break;
        }
        page += 1;
      }
      setCurrencyList(collected);
    } catch (e) {
      showErrorMessage(
        `Fetching currency list failed:${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsCurrencyListLoading(false);
    }
  }, []);

  const fetchProcessorMatrix = useCallback(async () => {
    try {
      const processorMatrix = await processorService.getProcessorMatrix();
      setCapabilityMatrix(processorMatrix);
    } catch (e) {
      showErrorMessage(
        `Fetching processor capability matrix failed:${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  useEffect(() => {
    // Each of the three fetchers try/catches into showErrorMessage, so these
    // only fire if that reporting itself throws. They go to the dev logger
    // rather than a second toast: the admin has already been told about the
    // real failure, and if showErrorMessage is what threw, calling it again
    // would only throw again. Attached because an unhandled rejection here
    // would leave the corridor table silently empty, and a missing row is a
    // corridor nobody can see or edit.
    fetchProcessorAll().catch(logger.error);
    fetchCurrencyList().catch(logger.error);
    fetchProcessorMatrix().catch(logger.error);
  }, [fetchProcessorAll, fetchCurrencyList, fetchProcessorMatrix]);

  const handleUpdateProcessor = useCallback(
    async (processorRequest: UpdateProcessorRequest) => {
      try {
        setError(null);
        const {target, currency, type, channel, processorName, processors} =
          processorRequest;
        if (!currency?.id || !target || !type || !channel || !processorName) {
          showErrorMessage(
            `Updating processor failed: Some required fields are missing`,
          );
          return;
        }
        if (!validateProcessor(processorName)) {
          showErrorMessage(`Invalid processor: ${processorName}`);
          return;
        }
        const invalidItems = processors?.filter((p) => !validateProcessor(p));
        if (invalidItems?.length) {
          showErrorMessage(`Invalid processors: ${invalidItems.join(', ')}`);
          return;
        }
        // Send only the audience subtree being edited, not the whole document
        // read back from the list endpoint. Mongoose wraps a non-operator
        // update in `$set`, so the other targets and every unrelated field are
        // preserved server-side, and this keeps identity and timestamp fields
        // out of a write that has no business carrying them.
        //
        // `$set` replaces the target subtree wholesale though, so the existing
        // channels under it have to be carried. Deep-cloned because lodash
        // `set` mutates in place, and a shallow copy would rewrite the
        // currency object still held in state.
        const payload = {
          [target]: stripRestrictedProcessors(
            cloneDeep(currency?.[target] ?? {}),
            target,
          ),
        };
        set(
          payload,
          `${target}.${type}.${channel}.currentProcessor`,
          processorName,
        );
        if (processors !== undefined) {
          set(payload, `${target}.${type}.${channel}.processors`, processors);
        }
        const savedCurrency = await currencyService.saveOne(
          currency?.id,
          payload,
        );
        if (savedCurrency) {
          showSuccessMessage('Currency updated successfully');
          setTimeout(() => window.location.reload(), 1000);
        } else {
          // The endpoint responds 200 with an empty body when the `_id`
          // filter matches no currency (stale id, deleted currency), so a
          // resolved-but-falsy response means nothing was saved.
          showErrorMessage('Updating processor failed: currency not found');
        }
      } catch (e) {
        showErrorMessage(
          `Updating processor failed:${getErrorMessage(e, String(e))}`,
        );
      }
    },

    [],
  );

  const handleRemoveProcessor = useCallback(
    async (removeRequest: RemoveProcessorRequest) => {
      try {
        setError(null);
        const {target, currency, type, channel, processorName} = removeRequest;
        if (!currency?.id || !target || !type || !channel) {
          showErrorMessage(
            `Removing processor failed: Some required fields are missing`,
          );
          return;
        }
        // Same as the update path: only the audience subtree is sent, cloned so
        // lodash `set` does not mutate the currency object held in state.
        const payload = {
          [target]: stripRestrictedProcessors(
            cloneDeep(currency?.[target] ?? {}),
            target,
          ),
        };
        if (processorName) {
          // Remove a specific processor from the processors[] array
          const currentProcessors: AfriexTransactionProcessor[] =
            currency?.[target]?.[type]?.[channel]?.processors ?? [];
          const updatedProcessors = currentProcessors.filter(
            (p) => p !== processorName,
          );
          set(
            payload,
            `${target}.${type}.${channel}.processors`,
            updatedProcessors,
          );
        } else {
          // Clear the currentProcessor
          set(payload, `${target}.${type}.${channel}.currentProcessor`, '');
        }
        const savedCurrency = await currencyService.saveOne(
          currency?.id,
          payload,
        );
        if (savedCurrency) {
          showSuccessMessage('Currency updated successfully');
          setTimeout(() => window.location.reload(), 1000);
        } else {
          // Same as `handleUpdateProcessor` above: a resolved-but-falsy
          // response means the `_id` filter matched no currency.
          showErrorMessage('Removing processor failed: currency not found');
        }
      } catch (e) {
        showErrorMessage(
          `Removing processor failed:${getErrorMessage(e, String(e))}`,
        );
      }
    },

    [],
  );

  const toggleProcessorUpdateForm = useCallback(() => {
    setShouldShowUpdateForm(!shouldShowUpdateForm);
  }, [shouldShowUpdateForm]);

  const showProcessorUpdateForm = useCallback(
    (currency: AfriexCurrency) => {
      setSelectedCurrency(currency);
      toggleProcessorUpdateForm();
    },
    [toggleProcessorUpdateForm],
  );

  const sortedCurrencyList = orderBy(currencyList, 'code');

  return {
    error,
    filteredCountryList,
    countryCodeByCurrency,
    // The rows are the currency list, so the table is not loaded until it is;
    // otherwise the empty-state message flashes before the fetch lands.
    isLoading: isCountryListLoading || isCurrencyListLoading,
    countryList: activeCountries,
    handleUpdateProcessor,
    handleRemoveProcessor,
    capabilityMatrix,
    processorsAll,
    selectedCurrency,
    shouldShowUpdateForm,
    showProcessorUpdateForm,
    toggleProcessorUpdateForm,
    currencyList: sortedCurrencyList,
  };
};

export default useProcessorList;
